/*
 * Import ohne Trello (0.29): „Aufgaben als Text“, CSV/Excel und die KaBa-Datei.
 * Ohne zod, damit der Browser die Vorschau selbst berechnet; der Server prüft den fertigen Plan
 * (importPlanSchema bzw. kabaBoardFileSchema in schemas.ts).
 */
import { allDayIso, localDateKey } from './dates.js';
import { LABEL_COLORS, type LabelColor } from './types.js';
import type { ImportCard, ImportPlan } from './trello.js';
import type { BoardTemplateData, BoardTemplateSummary } from './templates.js';

export const IMPORT_LIMITS = { columns: 50, labels: 50, cards: 5000, checklist: 100, members: 100 } as const;

export interface ParsedImport {
  plan: ImportPlan;
  /** Hinweise für die Vorschau, z. B. „2 Daten nicht erkannt“ */
  warnings: string[];
  /** CSV: erkannte Spalten („Titel ← Aufgabe“) */
  mapping?: { field: CsvField; header: string }[];
  /** 0.71: erkannter Export (Herkunft im Kartenverlauf) */
  source?: 'asana' | 'jira' | 'todoist' | 'ticktick';
}

/* ---------- Daten ---------- */

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Datum aus „3.11.2026“, „03.11.26“, „3.11.“, „2026-11-03“, „11/03/2026“ (US), optional mit Uhrzeit „14:30“.
 * Ohne Uhrzeit: ganztägig. Ohne Jahr: das nächste Vorkommen (bis 30 Tage zurück zählt als dieses Jahr).
 */
const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, mär: 3, apr: 4, may: 5, mai: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, okt: 10, nov: 11, dec: 12, dez: 12,
};

export function parseImportDate(raw: string, now = new Date()): { iso: string; allDay: boolean } | null {
  const s = raw.trim();
  if (!s) return null;
  let y: number, m: number, d: number;
  let rest: string;
  let mt: RegExpExecArray | null;
  if ((mt = /^(\d{4})-(\d{1,2})-(\d{1,2})(.*)$/.exec(s))) {
    [y, m, d, rest] = [Number(mt[1]), Number(mt[2]), Number(mt[3]), mt[4]!];
  } else if ((mt = /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})?(.*)$/.exec(s))) {
    d = Number(mt[1]);
    m = Number(mt[2]);
    rest = mt[4]!;
    if (mt[3]) y = mt[3].length === 2 ? 2000 + Number(mt[3]) : Number(mt[3]);
    else {
      y = now.getFullYear();
      const cand = new Date(y, m - 1, d);
      if (cand.getTime() < now.getTime() - 30 * 86_400_000) y += 1;
    }
  } else if ((mt = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(.*)$/.exec(s))) {
    [m, d, y, rest] = [Number(mt[1]), Number(mt[2]), Number(mt[3]), mt[4]!];
  } else if ((mt = /^(\d{1,2})[/.\- ]([A-Za-zäÄ]{3,4})\.?[/.\- ](\d{2,4})(.*)$/.exec(s)) && MONTHS[mt[2]!.slice(0, 3).toLowerCase()]) {
    // Jira: 23/Oct/26 9:30 AM bzw. 23/Okt/26 09:30
    d = Number(mt[1]);
    m = MONTHS[mt[2]!.slice(0, 3).toLowerCase()]!;
    y = mt[3]!.length === 2 ? 2000 + Number(mt[3]) : Number(mt[3]);
    rest = mt[4]!;
  } else return null;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1970 || y > 2200) return null;
  const check = new Date(y, m - 1, d);
  if (check.getMonth() !== m - 1) return null; // 31.2.
  const time = /^(?:\s+|T)(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*([AaPp])\.?[Mm]\.?)?/.exec(rest);
  if (time) {
    let h = Number(time[1]);
    if (time[3]) {
      if (h < 1 || h > 12) return null;
      h = (h % 12) + (/[Pp]/.test(time[3]) ? 12 : 0);
    }
    const min = Number(time[2]);
    if (h > 23 || min > 59) return null;
    return { iso: new Date(y, m - 1, d, h, min).toISOString(), allDay: false };
  }
  if (rest.trim() && !/^(?:\s+|T)00:00(:00)?(\.\d+)?Z?$/.test(rest)) return null;
  return { iso: allDayIso(`${y}-${pad(m)}-${pad(d)}`), allDay: true };
}

/* ---------- Gemeinsame Bausteine ---------- */

class PlanBuilder {
  columns: ImportPlan['columns'] = [];
  labels: ImportPlan['labels'] = [];
  cards: ImportCard[] = [];
  warnings = new Set<string>();
  members: ImportPlan['members'] = [];
  private colByName = new Map<string, string>();
  private labelByName = new Map<string, string>();
  private memberByName = new Map<string, string>();

  /** Personen aus der Datei (z. B. Planner „Zugewiesen an“) – zugeordnet wird beim Import */
  member(name: string): string | null {
    const n = name.trim().slice(0, 120);
    if (!n) return null;
    const k = n.toLowerCase();
    const hit = this.memberByName.get(k);
    if (hit) return hit;
    if (this.members.length >= IMPORT_LIMITS.members) return null;
    const key = `m${this.members.length}`;
    this.members.push({ key, name: n, username: '' });
    this.memberByName.set(k, key);
    return key;
  }

  column(title: string): string {
    const t = title.trim().slice(0, 200) || 'Offen';
    const k = t.toLowerCase();
    const hit = this.colByName.get(k);
    if (hit) return hit;
    if (this.columns.length >= IMPORT_LIMITS.columns) {
      this.warnings.add(`Mehr als ${IMPORT_LIMITS.columns} Spalten – weitere Karten landen in der letzten Spalte.`);
      return this.columns.at(-1)!.key;
    }
    const key = `c${this.columns.length}`;
    this.columns.push({ key, title: t });
    this.colByName.set(k, key);
    return key;
  }

  label(name: string): string | null {
    const n = name.trim().slice(0, 40);
    if (!n) return null;
    const k = n.toLowerCase();
    const hit = this.labelByName.get(k);
    if (hit) return hit;
    if (this.labels.length >= IMPORT_LIMITS.labels) {
      this.warnings.add(`Mehr als ${IMPORT_LIMITS.labels} Labels – weitere werden weggelassen.`);
      return null;
    }
    const colors = LABEL_COLORS.filter((c) => c !== 'gray');
    const key = `l${this.labels.length}`;
    this.labels.push({ key, name: n, color: colors[this.labels.length % colors.length] as LabelColor });
    this.labelByName.set(k, key);
    return key;
  }

  card(columnKey: string, c: Partial<ImportCard> & { title: string }): ImportCard | null {
    if (this.cards.length >= IMPORT_LIMITS.cards) {
      this.warnings.add(`Höchstens ${IMPORT_LIMITS.cards.toLocaleString('de-DE')} Karten je Import – der Rest wird weggelassen.`);
      return null;
    }
    const checklist = (c.checklist ?? []).filter((x) => x.text.trim());
    if (checklist.length > IMPORT_LIMITS.checklist) this.warnings.add(`Checklisten werden nach ${IMPORT_LIMITS.checklist} Punkten gekürzt.`);
    const card: ImportCard = {
      key: `k${this.cards.length}`,
      columnKey,
      title: c.title.trim().slice(0, 500) || 'Ohne Titel',
      description: c.description ?? '',
      attachments: [],
      labelKeys: c.labelKeys ?? [],
      memberKeys: c.memberKeys ?? [],
      ...(c.priority ? { priority: c.priority } : {}),
      dueAt: c.dueAt ?? null,
      startAt: c.startAt ?? null,
      allDay: c.allDay ?? true,
      completed: !!c.completed,
      archived: false,
      createdAt: null,
      checklist: checklist.slice(0, IMPORT_LIMITS.checklist).map((x) => ({ text: x.text.trim().slice(0, 1000), done: x.done })),
      comments: [],
    };
    this.cards.push(card);
    return card;
  }

  plan(title: string): ImportPlan {
    if (!this.columns.length) this.column('Offen');
    return { title: title.slice(0, 200), description: '', columns: this.columns, labels: this.labels, members: this.members, cards: this.cards };
  }
}

/* ---------- Aufgaben als Text ---------- */

const BULLET = /^(?:[-*•–]\s+|\d{1,3}[.)]\s+)/;
const CHECK = /^\[( |x|X)\]\s*/;
const LABEL_TOKEN = /(^|\s)#([\p{L}\p{N}][\p{L}\p{N}_-]*)/gu;
const DATE_TOKEN = /(^|\s)@(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}\.\d{1,2}\.(?:\d{2,4})?)(?:\s+(\d{1,2}:\d{2}))?(?=\s|$)/;

/** Erkennt eingefügte Tabellen (Excel, Google Sheets): Tabulatoren in mehreren Zeilen */
export function looksLikeTable(text: string): boolean {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return lines.length >= 1 && lines.filter((l) => l.includes('\t')).length >= Math.max(1, Math.ceil(lines.length * 0.6));
}

/**
 * Eine Zeile pro Karte. Spalten mit „## Überschrift“ (oder einer Zeile, die mit „:“ endet),
 * der Boardname mit „# Name“. Eingerückte Zeilen gehören zur Karte darüber: „[ ]“, „[x]“ oder „-“
 * werden Checklistenpunkte, alles andere Beschreibung. In der Kartenzeile: „#Label“, „@3.11.“ als Frist
 * (optional mit Uhrzeit), „[x]“ am Anfang = erledigt. Tabellen (Tabulatoren) gehen an den CSV-Leser.
 */
export function parseTextImport(text: string, now = new Date()): ParsedImport {
  if (looksLikeTable(text)) return parseCsvImport(text, now, '\t');
  const b = new PlanBuilder();
  let boardTitle = '';
  let col: string | null = null;
  let current: ImportCard | null = null;
  let badDates = 0;
  const desc: Map<ImportCard, string[]> = new Map();

  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    if (!raw.trim()) continue;
    const indented = /^(\s{2,}|\t)/.test(raw);
    const line = raw.trim();
    if (indented && current) {
      const item = line.replace(BULLET, '');
      const chk = CHECK.exec(item);
      if (chk || BULLET.test(line)) {
        current.checklist.push({ text: item.replace(CHECK, ''), done: !!chk && chk[1] !== ' ' });
      } else {
        desc.get(current)!.push(line);
      }
      continue;
    }
    const h = /^(#{1,3})\s+(.+)$/.exec(line);
    if (h) {
      if (h[1] === '#' && !boardTitle && !b.cards.length && !b.columns.length) boardTitle = h[2]!.trim();
      else col = b.column(h[2]!.replace(/:$/, ''));
      current = null;
      continue;
    }
    if (/^[^-*•–\d[#][^:]{0,80}:$/.test(line) && !BULLET.test(line)) {
      col = b.column(line.slice(0, -1));
      current = null;
      continue;
    }
    let title = line.replace(BULLET, '');
    let completed = false;
    const chk = CHECK.exec(title);
    if (chk) {
      completed = chk[1] !== ' ';
      title = title.slice(chk[0].length);
    }
    const labelKeys: string[] = [];
    title = title.replace(LABEL_TOKEN, (_m, pre: string, name: string) => {
      const k = b.label(name.replace(/_/g, ' '));
      if (k && !labelKeys.includes(k)) labelKeys.push(k);
      return pre;
    });
    let dueAt: string | null = null;
    let allDay = true;
    const dm = DATE_TOKEN.exec(title);
    if (dm) {
      const parsed = parseImportDate(dm[2]! + (dm[3] ? ` ${dm[3]}` : ''), now);
      if (parsed) {
        dueAt = parsed.iso;
        allDay = parsed.allDay;
        title = title.replace(dm[0], dm[1]!);
      } else badDates++;
    }
    current = b.card(col ?? b.column('Offen'), { title: title.replace(/\s{2,}/g, ' '), labelKeys, dueAt, allDay, completed });
    if (current) desc.set(current, []);
  }
  for (const [card, lines] of desc) card.description = lines.join('\n');
  if (badDates) b.warnings.add(`${badDates} Datum${badDates === 1 ? '' : 'sangaben'} nicht erkannt – sie bleiben im Titel.`);
  return { plan: b.plan(boardTitle || 'Importiertes Board'), warnings: [...b.warnings] };
}

/* ---------- CSV ---------- */

export type CsvField = 'title' | 'column' | 'description' | 'labels' | 'due' | 'start' | 'checklist' | 'completed' | 'progress' | 'priority' | 'assignees';
export const CSV_FIELD_LABELS: Record<CsvField, string> = {
  title: 'Titel',
  column: 'Spalte',
  description: 'Beschreibung',
  labels: 'Labels',
  due: 'Fällig',
  start: 'Start',
  checklist: 'Checkliste',
  completed: 'Erledigt',
  progress: 'Fortschritt',
  priority: 'Priorität',
  assignees: 'Zuständig',
};

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');

const ALIASES: Record<CsvField, string[]> = {
  title: ['titel', 'title', 'name', 'aufgabe', 'aufgaben', 'karte', 'kartentitel', 'task', 'betreff', 'summary', 'cardname', 'thema', 'taskname', 'aufgabenname', 'aufgabentitel', 'tasktitle'],
  // Planner: Bucket = Spalte (Status/Fortschritt ist dort eine eigene Angabe)
  column: ['spalte', 'liste', 'status', 'list', 'listname', 'column', 'phase', 'stage', 'stand', 'bucket', 'bucketname', 'bucketame', 'sectioncolumn', 'section', 'abschnitt'],
  description: ['beschreibung', 'description', 'notiz', 'notizen', 'notes', 'details', 'desc', 'carddescription', 'bemerkung', 'bemerkungen', 'kommentar'],
  labels: ['labels', 'label', 'tags', 'tag', 'etiketten', 'kategorie', 'kategorien', 'category', 'bereich', 'bezeichnungen', 'bezeichnung'],
  due: ['faellig', 'faelligam', 'faelligkeit', 'faelligkeitsdatum', 'frist', 'due', 'duedate', 'deadline', 'termin', 'datum', 'enddatum', 'ende', 'bis'],
  start: ['start', 'startdatum', 'beginn', 'startdate', 'ab', 'von'],
  checklist: ['checkliste', 'checklist', 'checklisten', 'unteraufgaben', 'subtasks', 'todos', 'schritte', 'checklistitems', 'checklistenelemente', 'pruefliste', 'prueflistenelemente', 'checklistenpunkte'],
  completed: ['erledigt', 'done', 'completed', 'abgeschlossen', 'fertig'],
  progress: ['fortschritt', 'progress', 'bearbeitungsstand'],
  priority: ['prioritaet', 'priority', 'prio', 'wichtigkeit', 'dringlichkeit'],
  assignees: ['zugewiesenan', 'zugewiesenzu', 'zugewiesen', 'assignedto', 'assignee', 'assignees', 'zustaendig', 'verantwortlich', 'bearbeiter', 'owner', 'members', 'mitglieder'],
};
const FIELD_BY_ALIAS = new Map(Object.entries(ALIASES).flatMap(([f, list]) => list.map((a) => [a, f as CsvField])));

/** Trennzeichen der ersten Zeile: Semikolon (deutsches Excel), Komma oder Tabulator */
export function detectDelimiter(text: string): ';' | ',' | '\t' {
  // 0.72: Metadaten über der Tabelle (TickTick) – die Zeile mit den meisten Trennzeichen unter den ersten 15 zählt
  const lines = text.split(/\r?\n/).filter((l) => l.trim()).slice(0, 15);
  const count = (ch: string) => Math.max(0, ...lines.map((l) => l.replace(/"[^"]*"/g, '').split(ch).length - 1));
  const c = { ';': count(';'), ',': count(','), '\t': count('\t') };
  const [best, n] = Object.entries(c).sort((a, b) => b[1] - a[1])[0]!;
  return (n > 0 ? best : ';') as ';' | ',' | '\t';
}

/** RFC 4180: Anführungszeichen, verdoppelte Anführungszeichen, Zeilenumbrüche in Feldern */
export function parseCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const src = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
    } else if (ch === '"' && !cell.trim()) {
      quoted = true;
      cell = '';
    } else if (ch === delimiter) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim()));
}

const TRUE = new Set(['ja', 'j', 'yes', 'y', 'x', 'true', 'wahr', '1', 'erledigt', 'done', 'fertig', '✓', '✔']);

export function parseCsvImport(text: string, now = new Date(), delimiter?: string): ParsedImport {
  return parseTableImport(parseCsv(text, delimiter ?? detectDelimiter(text)), now);
}

const PRIORITY: Record<string, number> = {
  dringend: 4, urgent: 4, kritisch: 4, critical: 4, sehrhoch: 4,
  wichtig: 3, important: 3, hoch: 3, high: 3,
  mittel: 2, medium: 2, normal: 2,
  niedrig: 1, low: 1, gering: 1,
  keine: 0, none: 0,
  // Jira
  highest: 4, blocker: 4, hoechste: 4, major: 3, hoechst: 4, lowest: 1, minor: 1, trivial: 1, niedrigste: 1,
};
/** „Abgeschlossen“, „Completed“, „100 %“ … */
const DONE_PROGRESS = new Set(['abgeschlossen', 'completed', 'complete', 'erledigt', 'done', 'fertig', '100', '100prozent']);

/** Überschriftenzeile finden: Planner & Co. schreiben Plan-Angaben über die Tabelle */
function findHeader(rows: string[][]): { at: number; idx: Map<CsvField, number> } | null {
  for (let r = 0; r < Math.min(rows.length, 15); r++) {
    const idx = new Map<CsvField, number>();
    rows[r]!.forEach((h, i) => {
      let f = FIELD_BY_ALIAS.get(norm(h));
      // Planner: Bucket ist die Spalte, „Status“ daneben ist der Fortschritt
      if (f === 'column' && idx.has('column')) f = 'progress';
      if (f && !idx.has(f)) idx.set(f, i);
    });
    // Eine echte Überschriftenzeile hat einen Titel und mindestens eine weitere bekannte Angabe
    if (idx.has('title') && idx.size >= 2) return { at: r, idx };
  }
  return null;
}

/**
 * Tabelle (CSV oder Excel-Blatt) als Board-Plan. Erkennt Überschriften auf Deutsch und Englisch,
 * auch den Excel-Export aus Microsoft Planner (Bucket → Spalte, Fortschritt → erledigt,
 * Priorität, Checkliste und Bezeichnungen mit „;“, Zugewiesen an → Personen zum Zuordnen).
 */
export function parseTableImport(rows: string[][], now = new Date()): ParsedImport {
  const b = new PlanBuilder();
  const filled = (r: string[]) => r.some((c) => c.trim());
  // Führende Leerzeilen weg; Leerzeilen in der Tabelle selbst werden unten übersprungen
  while (rows.length && !filled(rows[0]!)) rows = rows.slice(1);
  if (!rows.some(filled)) return { plan: b.plan('Importiertes Board'), warnings: ['Keine Zeilen gefunden.'], mapping: [] };
  // 0.72: Todoist/TickTick haben eigene Aufbauten (Abschnittszeilen bzw. Metadaten über der Tabelle)
  const exp = findExport(rows);
  if (exp) return exp.kind === 'todoist' ? parseTodoist(rows, exp.at, now) : parseTickTick(rows, exp.at, now);
  let found = findHeader(rows);
  let body: string[][];
  let header: string[];
  if (found) {
    header = rows[found.at]!;
    body = rows.slice(found.at + 1).filter(filled);
  } else {
    rows = rows.filter(filled);
    header = rows[0]!;
    const idx = new Map<CsvField, number>();
    header.forEach((h, i) => {
      const f = FIELD_BY_ALIAS.get(norm(h));
      if (f && !idx.has(f)) idx.set(f, i);
    });
    body = rows.slice(1);
    if (idx.size) {
      // Überschriften erkannt, aber kein Titel: die erste nicht zugeordnete Spalte
      const free = header.findIndex((_h, i) => ![...idx.values()].includes(i));
      if (free >= 0) idx.set('title', free);
    } else {
      // Keine Überschriften: erste Spalte = Titel, alle Zeilen sind Karten
      idx.set('title', 0);
      body = rows;
      b.warnings.add('Keine Überschriften erkannt – die erste Spalte wird als Kartentitel verwendet.');
    }
    found = { at: -1, idx };
  }
  const idx = found.idx;
  const noHeader = body === rows;
  const mapping = [...idx.entries()]
    .sort((a, b2) => a[1] - b2[1])
    .map(([field, i]) => ({ field, header: noHeader ? `Spalte ${i + 1}` : header[i]!.trim() || `Spalte ${i + 1}` }));
  const get = (r: string[], f: CsvField) => {
    const i = idx.get(f);
    return i === undefined ? '' : (r[i] ?? '').trim();
  };
  // 0.71: Asana- und Jira-Export erkennen (Kennungen, Unteraufgaben, Abhängigkeiten, mehrere Label-Spalten)
  const hn = noHeader ? [] : header.map(norm);
  const source: 'asana' | 'jira' | null = hn.includes('taskid') && (hn.includes('sectioncolumn') || hn.includes('projects'))
    ? 'asana'
    : hn.includes('issuekey') && hn.includes('summary')
      ? 'jira'
      : null;
  const all = (pred: (h: string) => boolean) => hn.flatMap((h, i) => (pred(h) ? [i] : []));
  const labelCols = noHeader ? [] : all((h) => FIELD_BY_ALIAS.get(h) === 'labels');
  const idCols = all((h) => ['taskid', 'issuekey', 'issueid', 'vorgangsschluessel', 'vorgangsid'].includes(h));
  const parentCols = all((h) => ['parenttask', 'parent', 'parentid', 'uebergeordneteaufgabe', 'uebergeordnetervorgang'].includes(h));
  const blockedCols = all((h) => ['blockedbydependencies', 'blockedby', 'wartetauf'].includes(h) || h.startsWith('inwardissuelinkblocks'));
  const doneAtCols = all((h) => ['completedat', 'completiondate', 'resolved', 'resolutiondate', 'erledigtam', 'abgeschlossenam', 'geloest'].includes(h));
  const keyCol = hn.indexOf('issuekey');
  const cells = (r: string[], cols: number[]) => cols.flatMap((i) => (r[i] ?? '').split(/[,;|\n]/)).map((x) => x.trim()).filter(Boolean);
  const JIRA_DONE = new Set(['done', 'closed', 'resolved', 'erledigt', 'fertig', 'geschlossen', 'geloest', 'abgeschlossen']);

  type Row = { r: string[]; title: string; ids: string[]; parent: string; blockedBy: string[]; card: ImportCard | null; completed: boolean };
  const parsed: Row[] = [];
  let badDates = 0;
  let empty = 0;
  for (const r of body) {
    const title = get(r, 'title');
    if (!title) {
      empty++;
      continue;
    }
    const doneAt = cells(r, doneAtCols).length > 0;
    const status = norm(get(r, 'column'));
    const completed =
      TRUE.has(get(r, 'completed').toLowerCase()) || DONE_PROGRESS.has(norm(get(r, 'progress'))) || doneAt || (source === 'jira' && JIRA_DONE.has(status));
    parsed.push({ r, title, ids: idCols.map((i) => (r[i] ?? '').trim()).filter(Boolean), parent: cells(r, parentCols)[0] ?? '', blockedBy: cells(r, blockedCols), card: null, completed });
  }
  // Unteraufgaben: Zeile mit übergeordneter Aufgabe (Kennung oder Titel), die im Export vorkommt
  const byId = new Map<string, Row>();
  for (const p of parsed) for (const id of p.ids) byId.set(id, p);
  const byTitle = new Map<string, Row>();
  for (const p of parsed) if (!byTitle.has(p.title.toLowerCase())) byTitle.set(p.title.toLowerCase(), p);
  const parentOf = (p: Row): Row | null => {
    if (!p.parent) return null;
    const hit = byId.get(p.parent) ?? byTitle.get(p.parent.toLowerCase()) ?? null;
    return hit && hit !== p && !hit.parent ? hit : null;
  };
  let subtasks = 0;
  for (const p of parsed) {
    if (parentOf(p)) continue;
    const r = p.r;
    const col = b.column(get(r, 'column') || 'Offen');
    const labelKeys = (labelCols.length ? cells(r, labelCols) : get(r, 'labels').split(/[,;|]/))
      .map((l) => b.label(l))
      .filter((k): k is string => !!k);
    const date = (f: 'due' | 'start') => {
      const v = get(r, f);
      if (!v) return null;
      const d2 = parseImportDate(v, now);
      if (!d2) badDates++;
      return d2;
    };
    const due = date('due');
    const start = date('start');
    const checklist = get(r, 'checklist')
      .split(/\n|\||;/)
      .map((t) => t.trim().replace(BULLET, ''))
      .filter(Boolean)
      .map((t) => {
        const m = CHECK.exec(t);
        return { text: t.replace(CHECK, ''), done: !!m && m[1] !== ' ' };
      });
    const prioRaw = norm(get(r, 'priority'));
    const priority = prioRaw ? (PRIORITY[prioRaw] ?? (/^[0-4]$/.test(prioRaw) ? Number(prioRaw) : 0)) : 0;
    const memberKeys = get(r, 'assignees')
      .split(/;|\n/)
      .map((n2) => b.member(n2))
      .filter((k): k is string => !!k);
    const jiraKey = source === 'jira' && keyCol >= 0 ? (r[keyCol] ?? '').trim() : '';
    const description = [get(r, 'description'), jiraKey ? `Jira: ${jiraKey}` : ''].filter(Boolean).join('\n\n');
    p.card = b.card(col, {
      title: p.title,
      description,
      labelKeys: [...new Set(labelKeys)],
      memberKeys: [...new Set(memberKeys)].slice(0, 20),
      priority,
      dueAt: due?.iso ?? null,
      startAt: start?.iso ?? null,
      allDay: due ? due.allDay : true,
      completed: p.completed,
      checklist,
    });
  }
  for (const p of parsed) {
    const parent = parentOf(p);
    if (!parent?.card) continue;
    if (parent.card.checklist.length >= IMPORT_LIMITS.checklist) continue;
    parent.card.checklist.push({ text: p.title.slice(0, 1000), done: p.completed });
    subtasks++;
  }
  // Abhängigkeiten über Kennungen (Asana „Blocked By“, Jira „Inward issue link (Blocks)“)
  let deps = 0;
  for (const p of parsed) {
    if (!p.card || !p.blockedBy.length) continue;
    const keys = [...new Set(p.blockedBy.map((x) => (byId.get(x) ?? byTitle.get(x.toLowerCase()))?.card?.key).filter((k): k is string => !!k && k !== p.card!.key))].slice(0, 50);
    if (keys.length) {
      p.card.dependsOn = keys;
      deps += keys.length;
    }
  }
  if (source) b.warnings.add(`${source === 'asana' ? 'Asana' : 'Jira'}-Export erkannt.${subtasks ? ` ${subtasks} Unteraufgabe${subtasks === 1 ? ' wird' : 'n werden'} zur Checkliste der übergeordneten Karte.` : ''}${deps ? ` ${deps} Abhängigkeit${deps === 1 ? '' : 'en'} werden verknüpft.` : ''}`);
  if (badDates) b.warnings.add(`${badDates} Datum${badDates === 1 ? '' : 'sangaben'} nicht erkannt – sie werden weggelassen.`);
  if (empty) b.warnings.add(`${empty} Zeile${empty === 1 ? '' : 'n'} ohne Titel übersprungen.`);
  if (found.at > 0) b.warnings.add(`Die ersten ${found.at} Zeilen (z. B. Planname, Exportdatum) wurden übersprungen – die Tabelle beginnt in Zeile ${found.at + 1}.`);
  return { plan: b.plan('Importiertes Board'), warnings: [...b.warnings], mapping, ...(source ? { source } : {}) };
}

/* ---------- 0.72: Todoist und TickTick ---------- */

/** Kopfzeile eines Todoist- bzw. TickTick-Exports in den ersten Zeilen finden */
function findExport(rows: string[][]): { kind: 'todoist' | 'ticktick'; at: number } | null {
  for (let r = 0; r < Math.min(rows.length, 15); r++) {
    const h = rows[r]!.map(norm);
    if (h.includes('type') && h.includes('content') && h.includes('indent')) return { kind: 'todoist', at: r };
    if (h.includes('listname') && h.includes('title') && (h.includes('ischecklist') || h.includes('taskid'))) return { kind: 'ticktick', at: r };
  }
  return null;
}

/** Todoist-Priorität im CSV: 1 = p1 (dringend) … 4 = p4 (keine) */
const TODOIST_PRIO: Record<string, number> = { '1': 4, '2': 3, '3': 2, '4': 0 };
/** TickTick: 0 keine, 1 niedrig, 3 mittel, 5 hoch */
const TICKTICK_PRIO: Record<string, number> = { '0': 0, '1': 1, '3': 2, '5': 3 };
const LABEL_IN_TEXT = /(^|\s)@([\p{L}\p{N}_-]{1,40})/gu;

/**
 * Todoist-Projekt als CSV („Als Vorlage exportieren“ / „Projekt exportieren“): Zeilen mit TYPE section,
 * task und note. Abschnitte werden zu Spalten, Einrückung > 1 zur Checkliste der Aufgabe darüber,
 * Notizen zur Beschreibung, @Labels im Text zu Labels. Wiederkehrende Angaben („every monday“) bleiben als Hinweis.
 */
function parseTodoist(rows: string[][], at: number, now: Date): ParsedImport {
  const b = new PlanBuilder();
  const h = rows[at]!.map(norm);
  const col = (n: string) => h.indexOf(n);
  const g = (r: string[], n: string) => (col(n) >= 0 ? (r[col(n)] ?? '').trim() : '');
  let column: string | null = null;
  let last: ImportCard | null = null;
  let badDates = 0;
  let recurring = 0;
  let subtasks = 0;
  for (const r of rows.slice(at + 1)) {
    const type = g(r, 'type').toLowerCase();
    const content = g(r, 'content');
    if (!type || !content) continue;
    if (type === 'section') {
      column = b.column(content);
      last = null;
      continue;
    }
    if (type === 'note') {
      if (last) last.description = [last.description, content].filter(Boolean).join('\n\n');
      continue;
    }
    if (type !== 'task') continue;
    const labelKeys: string[] = [];
    const title = content
      .replace(LABEL_IN_TEXT, (_m, pre: string, name: string) => {
        const k = b.label(name.replace(/_/g, ' '));
        if (k && !labelKeys.includes(k)) labelKeys.push(k);
        return pre;
      })
      .replace(/\s{2,}/g, ' ')
      .trim();
    const indent = Number(g(r, 'indent') || '1');
    if (indent > 1 && last) {
      if (last.checklist.length < IMPORT_LIMITS.checklist) last.checklist.push({ text: title.slice(0, 1000), done: false });
      subtasks++;
      continue;
    }
    const dateRaw = g(r, 'deadline') || g(r, 'date');
    let due: { iso: string; allDay: boolean } | null = null;
    let note = '';
    if (dateRaw) {
      due = parseImportDate(dateRaw, now);
      if (!due) {
        if (/every|jeden|jede|alle|täglich|wöchentlich|monatlich|daily|weekly|monthly/i.test(dateRaw)) {
          recurring++;
          note = `Wiederholung in Todoist: „${dateRaw}“`;
        } else badDates++;
      }
    }
    const responsible = g(r, 'responsible');
    const memberKeys = responsible ? [b.member(responsible.replace(/\s*\(\d+\)$/, ''))].filter((k): k is string => !!k) : [];
    column ??= b.column('Aufgaben');
    last = b.card(column, {
      title: title || content,
      description: [g(r, 'description'), note].filter(Boolean).join('\n\n'),
      labelKeys,
      memberKeys,
      priority: TODOIST_PRIO[g(r, 'priority')] ?? 0,
      dueAt: due?.iso ?? null,
      allDay: due ? due.allDay : true,
    });
  }
  b.warnings.add(`Todoist-Export erkannt.${subtasks ? ` ${subtasks} Unteraufgabe${subtasks === 1 ? ' wird' : 'n werden'} zur Checkliste.` : ''}`);
  if (recurring) b.warnings.add(`${recurring} Wiederholung${recurring === 1 ? '' : 'en'} aus Todoist stehen als Hinweis in der Beschreibung – richte sie bei Bedarf unter „Wiederholung“ neu ein.`);
  if (badDates) b.warnings.add(`${badDates} Datum${badDates === 1 ? '' : 'sangaben'} nicht erkannt – sie werden weggelassen.`);
  return { plan: b.plan('Todoist-Projekt'), warnings: [...b.warnings], source: 'todoist' };
}

/** TickTick-Zeitpunkt („2026-10-29T23:00:00+0000“); ganztägig = Kalendertag in der Zeitzone des Geräts */
function tickDate(raw: string, allDay: boolean): { iso: string; allDay: boolean } | null {
  const s = raw.trim().replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  if (!s) return null;
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return null;
  const d = new Date(t);
  if (d.getFullYear() < 1970 || d.getFullYear() > 2200) return null;
  return allDay ? { iso: allDayIso(localDateKey(d)), allDay: true } : { iso: d.toISOString(), allDay: false };
}

/**
 * TickTick-Datensicherung (Einstellungen → Konto → Datensicherung, CSV): Metadaten oben, dann eine Zeile je
 * Aufgabe. Kanban-Spalte (sonst Liste) wird zur Spalte, Tags zu Labels, Checklisten-Inhalt (▫/▪) zur
 * Checkliste, Unteraufgaben (parentId) ebenfalls; Status 1/2 = erledigt.
 */
function parseTickTick(rows: string[][], at: number, now: Date): ParsedImport {
  void now;
  const b = new PlanBuilder();
  const h = rows[at]!.map(norm);
  const g = (r: string[], n: string) => (h.indexOf(n) >= 0 ? (r[h.indexOf(n)] ?? '').trim() : '');
  const body = rows.slice(at + 1).filter((r) => g(r, 'title'));
  const lists = new Set(body.map((r) => g(r, 'listname')).filter(Boolean));
  const byId = new Map<string, ImportCard>();
  const pending: { parent: string; title: string; done: boolean }[] = [];
  let badDates = 0;
  for (const r of body) {
    const done = ['1', '2'].includes(g(r, 'status'));
    const parent = g(r, 'parentid');
    const title = g(r, 'title');
    if (parent) {
      pending.push({ parent, title, done });
      continue;
    }
    // Mehrere Listen: Liste als Spalte; eine Liste mit Kanban-Spalten: diese
    const colName = (lists.size > 1 ? g(r, 'listname') : g(r, 'columnname') || g(r, 'listname')) || 'Aufgaben';
    const content = g(r, 'content');
    const isChecklist = /^(y|yes|true|1)$/i.test(g(r, 'ischecklist'));
    const checklist: { text: string; done: boolean }[] = [];
    const descLines: string[] = [];
    for (const line of content.split(/\r?\n/)) {
      const m = /^\s*([▫▪☐☑✓✔]|- \[( |x|X)\])\s*(.*)$/.exec(line);
      if (isChecklist && m && m[3]!.trim()) checklist.push({ text: m[3]!.trim(), done: m[1] === '▪' || m[1] === '☑' || m[1] === '✓' || m[1] === '✔' || (!!m[2] && m[2] !== ' ') });
      else descLines.push(line);
    }
    const allDay = !/^(false|0|n|no)$/i.test(g(r, 'isallday'));
    const due = g(r, 'duedate') ? tickDate(g(r, 'duedate'), allDay) : null;
    const start = g(r, 'startdate') ? tickDate(g(r, 'startdate'), allDay) : null;
    if ((g(r, 'duedate') && !due) || (g(r, 'startdate') && !start)) badDates++;
    const repeat = g(r, 'repeat');
    const labelKeys = g(r, 'tags')
      .split(/[,;]/)
      .map((t) => b.label(t.replace(/^#/, '')))
      .filter((k): k is string => !!k);
    const card = b.card(b.column(colName), {
      title,
      description: [descLines.join('\n').trim(), repeat ? `Wiederholung in TickTick: ${repeat}` : ''].filter(Boolean).join('\n\n'),
      labelKeys: [...new Set(labelKeys)],
      priority: TICKTICK_PRIO[g(r, 'priority')] ?? 0,
      dueAt: due?.iso ?? null,
      startAt: start && due ? start.iso : null,
      allDay: due ? due.allDay : true,
      completed: done,
      checklist,
    });
    const id = g(r, 'taskid');
    if (card && id) byId.set(id, card);
  }
  let subtasks = 0;
  for (const s2 of pending) {
    const parent = byId.get(s2.parent);
    if (parent && parent.checklist.length < IMPORT_LIMITS.checklist) {
      parent.checklist.push({ text: s2.title.slice(0, 1000), done: s2.done });
      subtasks++;
    } else b.card(b.column('Aufgaben'), { title: s2.title, completed: s2.done });
  }
  b.warnings.add(`TickTick-Datensicherung erkannt.${subtasks ? ` ${subtasks} Unteraufgabe${subtasks === 1 ? ' wird' : 'n werden'} zur Checkliste.` : ''}`);
  if (badDates) b.warnings.add(`${badDates} Datum${badDates === 1 ? '' : 'sangaben'} nicht erkannt – sie werden weggelassen.`);
  return { plan: b.plan(lists.size === 1 ? [...lists][0]! : 'TickTick'), warnings: [...b.warnings], source: 'ticktick' };
}

/* ---------- KaBa-Datei ---------- */

export const KABA_FILE_FORMAT = 'kaba-board';
export interface KabaBoardFile {
  format: typeof KABA_FILE_FORMAT;
  version: 1;
  exportedAt: string;
  title: string;
  description: string | null;
  template: BoardTemplateData;
}

export function isKabaBoardFile(x: unknown): x is KabaBoardFile {
  const o = x as Partial<KabaBoardFile> | null;
  return !!o && typeof o === 'object' && o.format === KABA_FILE_FORMAT && typeof o.template === 'object' && Array.isArray(o.template?.columns);
}

/* ---------- Vorschau für Import-Pläne (gleiche Form wie bei Vorlagen) ---------- */

export function planPreview(plan: ImportPlan, perColumn = 12): BoardTemplateSummary['preview'] {
  const labels = new Map(plan.labels.map((l) => [l.key, { name: l.name, color: l.color }]));
  return {
    columns: plan.columns.map((c) => {
      const cards = plan.cards.filter((x) => x.columnKey === c.key && !x.archived);
      return {
        title: c.title,
        wipLimit: null,
        entryRule: null,
        exitRule: null,
        total: cards.length,
        cards: cards.slice(0, perColumn).map((x) => ({
          title: x.title,
          labels: x.labelKeys.map((k) => labels.get(k)).filter((l): l is { name: string; color: LabelColor } => !!l),
          checklist: x.checklist.length,
          due: !!x.dueAt,
          recurring: false,
          done: x.completed,
        })),
      };
    }),
    labels: plan.labels.map((l) => ({ name: l.name, color: l.color })),
    fields: [],
    cardTemplates: [],
    automations: [],
    forms: [],
    views: [],
    overview: [],
  };
}
